import { Controller } from "@hotwired/stimulus";

/*
 * Fetches a turbo stream on an interval.
 *
 * Nothing tells it when to stop. The stream replaces this element, so whether
 * the replacement still declares the controller is what decides whether polling
 * continues - which keeps "is there anything left to wait for?" a server-side
 * question, ended by omission.
 *
 * Usage:
 *
 *   <div id="..." data-controller="poll" data-poll-url-value="<%= some_path %>">
 */
export default class extends Controller {
  static values = { url: String, interval: { type: Number, default: 2000 } };

  // Give up after a solid minute of failures rather than retry forever, so a
  // stream that never comes back doesn't leave the tab hammering the endpoint.
  static FAILURE_WINDOW_MS = 60_000;

  connect() {
    this.unmounted = false;
    this.failingSince = null;
    this.#schedule();
  }

  // Flagged as well as cleared: a fetch already in flight resumes after this
  // and would schedule a fresh timeout on a controller that is gone, leaving it
  // polling a detached element with nothing left to stop it.
  disconnect() {
    this.unmounted = true;
    clearTimeout(this.timeout);
  }

  /* Private */

  #schedule() {
    this.timeout = setTimeout(() => this.#tick(), this.intervalValue);
  }

  async #tick() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      });

      if (this.unmounted) return;

      if (response.ok) {
        this.failingSince = null;
        window.Turbo.renderStreamMessage(await response.text());
        return;
      }
    } catch {
      // Nothing worth surfacing: the page is correct as rendered, just not fresh.
    }

    if (this.unmounted) return;

    this.failingSince ??= Date.now();
    if (Date.now() - this.failingSince >= this.constructor.FAILURE_WINDOW_MS) return;

    this.#schedule();
  }
}
